import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listExamConfigs, listShortCodes, listSubjectConfigs, saveSubjectConfig, deleteSubjectConfig } from "../../api/examApi";
import "../Users/UsersPage.css";

const classApi = createLookupApi("classes");
const subjectApi = createLookupApi("subjects");

/** Which subjects are examined under a class×exam, and which short codes (with per-subject weight overrides) make up each subject's mark. */
export default function ExamSubjectConfigPage() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [shortCodes, setShortCodes] = useState([]);
  const [subjectConfigs, setSubjectConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [selectedShortCodes, setSelectedShortCodes] = useState({}); // shortCodeId -> {totalMark, passMark, acceptance}

  useEffect(() => {
    Promise.all([classApi.list(), subjectApi.list()]).then(([classList, subjectList]) => {
      setClasses(classList);
      setSubjects(subjectList);
      if (classList.length > 0) setClassId(String(classList[0].id));
    });
  }, []);

  useEffect(() => {
    if (!classId) return;
    Promise.all([listExamConfigs(classId), listShortCodes(classId)]).then(([configs, codes]) => {
      setExamConfigs(configs);
      setShortCodes(codes);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classId]);

  const load = () => {
    if (!examConfigId) {
      setSubjectConfigs([]);
      return;
    }
    setLoading(true);
    listSubjectConfigs(examConfigId)
      .then(setSubjectConfigs)
      .catch(() => setError("Failed to load subject configurations."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [examConfigId]);

  const toggleShortCode = (sc) => {
    setSelectedShortCodes((prev) => {
      const next = { ...prev };
      if (next[sc.shortCodeId]) {
        delete next[sc.shortCodeId];
      } else {
        next[sc.shortCodeId] = { totalMark: sc.totalMark, passMark: sc.passMark, acceptance: sc.acceptance };
      }
      return next;
    });
  };

  const updateShortCodeField = (id, field, value) => {
    setSelectedShortCodes((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const markConfigs = Object.entries(selectedShortCodes).map(([shortCodeId, cfg]) => ({
      shortCodeId: Number(shortCodeId),
      totalMark: Number(cfg.totalMark),
      passMark: Number(cfg.passMark),
      acceptance: Number(cfg.acceptance),
    }));
    try {
      await saveSubjectConfig({ examConfigId: Number(examConfigId), subjectId: Number(subjectId), markConfigs });
      setSuccess("Subject configuration saved.");
      setSubjectId("");
      setSelectedShortCodes({});
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove subject "${row.subjectName}" from this exam?`)) return;
    setError("");
    try {
      await deleteSubjectConfig(row.examSubjectConfigId);
      setSuccess("Removed.");
      setSubjectConfigs((prev) => prev.filter((s) => s.examSubjectConfigId !== row.examSubjectConfigId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const columns = [
    { key: "subjectName", label: "Subject" },
    {
      key: "markConfigs",
      label: "Short Codes",
      render: (row) => row.markConfigs.map((mc) => `${mc.shortCodeTitle} (${mc.totalMark})`).join(", "),
    },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Subject &amp; Mark Setup</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters" style={{ display: "flex", gap: 16 }}>
        <label>
          Class
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured for this class</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
      </div>

      {examConfigId && (
        <form onSubmit={handleSubmit} className="user-form" style={{ marginTop: 16, marginBottom: 24 }}>
          <label>Subject *</label>
          <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} required>
            <option value="" disabled>
              Select a subject
            </option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <label>Short Codes</label>
          {shortCodes.length === 0 && <p>No short codes defined for this class yet.</p>}
          {shortCodes.map((sc) => (
            <div key={sc.shortCodeId} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <input type="checkbox" checked={!!selectedShortCodes[sc.shortCodeId]} onChange={() => toggleShortCode(sc)} />
              <span style={{ minWidth: 100 }}>{sc.shortCodeTitle}</span>
              {selectedShortCodes[sc.shortCodeId] && (
                <>
                  <input
                    type="number"
                    style={{ width: 70 }}
                    title="Total Mark"
                    value={selectedShortCodes[sc.shortCodeId].totalMark}
                    onChange={(e) => updateShortCodeField(sc.shortCodeId, "totalMark", e.target.value)}
                  />
                  <input
                    type="number"
                    style={{ width: 70 }}
                    title="Pass Mark"
                    value={selectedShortCodes[sc.shortCodeId].passMark}
                    onChange={(e) => updateShortCodeField(sc.shortCodeId, "passMark", e.target.value)}
                  />
                  <input
                    type="number"
                    step="0.01"
                    style={{ width: 70 }}
                    title="Weight"
                    value={selectedShortCodes[sc.shortCodeId].acceptance}
                    onChange={(e) => updateShortCodeField(sc.shortCodeId, "acceptance", e.target.value)}
                  />
                </>
              )}
            </div>
          ))}

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={Object.keys(selectedShortCodes).length === 0}>
              Save Subject Configuration
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={subjectConfigs}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Remove
            </button>
          )}
        />
      )}
    </MainLayout>
  );
}
